<template>
  <calm-page extra-class="cashclear" :title="$t('lmg.cash_clear_title')">
    <template slot="sub">
      เงินสดในสาขา {{ getBranchCash.Amount }} · ข้อมูลล่าสุดเมื่อ
      {{ getBranchCash.UpdatedAt }}
    </template>
    <template slot="actions">
      <calm-button variant="primary" @click="handleTransferCash()">
        {{ $t("lmg.cash_out_branch") }}
      </calm-button>
    </template>

    <!-- ═══════════════ REPORT-TYPE TOGGLE (segmented chips) ═══════════════ -->
    <!-- Reskins the report-type el-select as Calm segmented chips. Bindings
         preserved: each chip writes searchParams.reportType (same v-model
         target) — 1 = cash held at staff, 2 = cash moved out of branch. -->
    <div class="calm__chips cashclear__modechips">
      <button
        v-for="option in reportType"
        :key="option.ID"
        type="button"
        class="calm__chipbtn"
        :class="{ 'is-active': searchParams.reportType == option.ID }"
        @click="searchParams.reportType = option.ID"
      >
        <span class="calm__chipbtn__dot"></span>
        {{ option.Name }}
      </button>
    </div>

    <!-- ═══════════════ SEARCH BAR ═══════════════ -->
    <calm-filter-bar
      :search-label="$t('search')"
      :show-reset="false"
      @search="handleSearch()"
    >
      <calm-field :label="$t('lmg.payment_date')">
        <flat-picker
          :config="{ allowInput: true }"
          class="form-control datepicker"
          v-model="searchParams.startDate"
        >
        </flat-picker>
      </calm-field>

      <calm-field :label="$t('lmg.payment_date')">
        <flat-picker
          :config="{ allowInput: true }"
          class="form-control datepicker"
          v-model="searchParams.endDate"
        >
        </flat-picker>
      </calm-field>

      <calm-field :label="$t('lmg.sort')">
        <el-select
          v-model="searchParams.sort"
          :placeholder="$t('lmg.sort')"
        >
          <el-option
            v-for="option in sort"
            :key="option.ID"
            :label="option.Name"
            :value="option.ID"
          >
          </el-option>
        </el-select>
      </calm-field>

      <calm-field
        v-if="searchParams.reportType == 1"
        :label="$t('lmg.recorder')"
      >
        <el-select
          v-model="searchParams.rpID"
          :placeholder="$t('lmg.sort')"
        >
          <el-option
            v-for="option in tablePerson"
            :key="option.ID"
            :label="option.FName + '  ' + option.LName"
            :value="option.ID"
          >
          </el-option>
        </el-select>
      </calm-field>
    </calm-filter-bar>

    <!-- ═══════════════ TABLE (skin the existing el-tables, §11) ═══════════════ -->
    <calm-table
      :total="total"
      :from="from"
      :to="to"
      :per-page.sync="pagination.perPage"
      :current-page.sync="pagination.currentPage"
      :per-page-options="pagination.perPageOptions"
      :per-page-label="$t('per_page') !== 'per_page' ? $t('per_page') : 'ต่อหน้า'"
      :count-label="$t('data')"
      :to-label="$t('to')"
      :of-label="$t('of')"
      :empty-title="$t('no_data') !== 'no_data' ? $t('no_data') : 'ไม่พบข้อมูล'"
    >
      <template slot="toolbar">
        <calm-field :label="$t('search')">
          <input
            v-model="searchQuery"
            type="text"
            class="ed"
            :placeholder="$t('search')"
          />
        </calm-field>
      </template>

      <template v-if="searchParams.reportType == 1">
        <el-table
          :data="queriedData"
          row-key="id"
          header-row-class-name="thead-light"
          @sort-change="sortChange"
          @selection-change="selectionChange"
        >
          <el-table-column :label="$t(`lmg.recorder`)" min-width="200">
            <div slot-scope="{ row }" class="d-flex">
              {{ row.PaymentRecordPersonFName }}
              {{ row.PaymentRecordPersonLName }}
            </div>
          </el-table-column>

          <el-table-column :label="$t(`lmg.amount`)" min-width="200">
            <div slot-scope="{ row }" class="d-flex">
              {{ FormatCurrency(row.Amount) }}
            </div>
          </el-table-column>

          <el-table-column :label="$t(`lmg.payment_date`)" min-width="200">
            <div slot-scope="{ row }" class="d-flex">
              {{ row.PaymentCreatedAt }}
            </div>
          </el-table-column>

          <el-table-column :label="$t(`lmg.cash_clear_recorder`)" min-width="200">
            <div slot-scope="{ row }" class="d-flex">
              {{ row.ClearByPersonFName }} {{ row.ClearByPersonLName }}
            </div>
          </el-table-column>

          <el-table-column :label="$t(`lmg.cash_clear_date`)" min-width="200">
            <template slot-scope="{ row }">
              <span class="calm__pill calm__pill--success">
                {{ row.CreatedAt }}
              </span>
            </template>
          </el-table-column>
        </el-table>
      </template>

      <template v-if="searchParams.reportType == 2">
        <el-table
          :data="getBranch"
          row-key="id"
          header-row-class-name="thead-light"
          @sort-change="sortChange"
          @selection-change="selectionChange"
        >
          <el-table-column :label="$t(`lmg.recorder`)" min-width="200">
            <div slot-scope="{ row }" class="d-flex">
              {{ row.RecordPersonFName }} {{ row.RecordPersonLName }}
            </div>
          </el-table-column>

          <el-table-column :label="$t(`lmg.amount`)" min-width="200">
            <div slot-scope="{ row }" class="d-flex">
              {{ FormatCurrency(row.Amount) }}
            </div>
          </el-table-column>

          <el-table-column :label="$t(`lmg.note`)" min-width="200">
            <div slot-scope="{ row }" class="d-flex">
              {{ row.Note }}
            </div>
          </el-table-column>

          <el-table-column :label="$t(`lmg.trans_date`)" min-width="200">
            <template slot-scope="{ row }">
              <span class="calm__pill calm__pill--neutral">
                {{ row.CreatedAt }}
              </span>
            </template>
          </el-table-column>
        </el-table>
      </template>
    </calm-table>

    <!-- ═══════════════ TRANSFER CASH MODAL ═══════════════ -->
    <calm-modal
      :show.sync="showTransferCashModal"
      :title="$t('lmg.cash_out_branch')"
      :show-footer="false"
    >
      <validation-observer v-slot="{ handleSubmit }" ref="formValidator">
        <form
          class="cashclear__form"
          role="form"
          @submit.prevent="handleSubmit(handleCreateTransferCash)"
        >
          <base-input
            :label="$t('lmg.amount')"
            :name="$t('lmg.amount')"
            rules="required"
            v-model="tranfer_cash.amount"
          ></base-input>

          <base-input
            :label="$t('lmg.note')"
            :name="$t('lmg.note')"
            rules="required"
            v-model="tranfer_cash.note"
          ></base-input>

          <div class="calm__modal__actions">
            <calm-button
              variant="ghost"
              @click="showTransferCashModal = false"
            >
              {{ $t("close") }}
            </calm-button>
            <calm-button variant="primary" native-type="submit">
              {{ $t("save") }}
            </calm-button>
          </div>
        </form>
      </validation-observer>
    </calm-modal>
  </calm-page>
</template>
<script>
import { Table, TableColumn, Select, Option } from "element-ui";
import RouteBreadCrumb from "@/components/Breadcrumb/RouteBreadcrumb";
import { BasePagination } from "@/components";
import clientPaginationMixin from "./PaginatedTables/clientPaginationMixin";
import swal from "sweetalert2";
import { mapGetters, mapActions } from "vuex";
import FileInput from "@/components/Inputs/FileInput";
import flatPicker from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";

export default {
  mixins: [clientPaginationMixin],
  components: {
    flatPicker,
    BasePagination,
    RouteBreadCrumb,
    FileInput,
    [Select.name]: Select,
    [Option.name]: Option,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn
  },
  computed: {
    ...mapGetters({
      getBranchCash: "Payment/getBranchCash",
      tableClear: "Payment/getClear",
      getBranch: "Payment/getBranch",
      tablePerson: "Person/getDataWithAll"
    }),
    tableData: function() {
      return this.tableClear;
    }
  },
  data() {
    return {
      propsToSearch: ["Name", "AcountNumber"],
      selectedRows: [],
      showAddModal: false,
      showTransferCashModal: false,
      searchParams: {
        startDate: "",
        endDate: "",
        sort: 1, //1=DESC
        tID: 0, // TransactionID
        rpID: 0, // พนักงานที่รับเงิน
        reportType: 1
      },
      tranfer_cash: {
        amount: "",
        note: ""
      },

      paidType: [
        { ID: 1, Name: "เงินสด" },
        { ID: 2, Name: "เช็ค" },
        { ID: 3, Name: "โอนเงิน" }
      ],
      paidStatus: [
        { ID: 0, Name: "All" },
        { ID: 1, Name: "จ่ายแล้ว" },
        { ID: 2, Name: "ยังไม่จ่าย" }
      ],
      sort: [
        { ID: 0, Name: "วันที่ล่าสุด" },
        { ID: 1, Name: "วันที่เริ่มต้น" }
      ],
      reportType: [
        { ID: 1, Name: "รายการเงินสดคงค้างที่พนักงาน" },
        { ID: 2, Name: "รายการโยกเงินสดออกจากสขา" }
      ]
    };
  },
  mounted() {
    this.listPerson();
    this.listBranchCash();

    this.searchParams.startDate = this.getStartDate();
    this.searchParams.endDate = this.getEndDate();
  },
  methods: {
    ...mapActions({
      listClear: "Payment/listClear",
      listBranchTransfer: "Payment/listBranchTransfer",
      listBranchCash: "Payment/listBranchCash",
      createBranchTransfer: "Payment/createBranchTransfer",
      listPerson: "Person/listData"
    }),
    handleSearch() {
      this.listBranchCash();
      if (this.searchParams.reportType == 1) {
        this.listClear(this.searchParams);
      } else {
        this.listBranchTransfer(this.searchParams);
      }
    },
    handleTransferCash() {
      this.showTransferCashModal = true;
      this.tranfer_cash.amount = "";
      this.tranfer_cash.note = "";
      this.$refs.formValidator.reset();
    },

    handleCreateTransferCash() {
      this.createBranchTransfer(this.tranfer_cash)
        .then(resp => {
          this.showTransferCashModal = false;
          this.listBranchTransfer(this.searchParams);
          this.listBranchCash();
          swal.fire({
            title: this.$t("edit_success"),
            text: ``,
            icon: "success",
            confirmButtonClass: "btn btn-success btn-fill",
            buttonsStyling: false
          });
        })
        .catch(err => {
          swal.fire({
            title: this.$t(err.response.data.Msg),
            text: this.getErrorText(err.response.data),
            icon: "error",
            confirmButtonClass: "btn btn-danger btn-fill",
            buttonsStyling: false
          });
        });
    },
    getPaidStatus(t) {
      let s = this.paidStatus.filter(f => {
        return f.ID == t;
      });

      return s[0].Name;
    },
    selectionChange(selectedRows) {
      this.selectedRows = selectedRows;
    },
    filesChange(files) {
      this.md.billfile = files[0];
    }
  }
};
</script>
<style scoped>
/* Cash-clearing — Calm skin. Namespaced under .cashclear so nothing leaks.
   Both el-tables are kept verbatim (§11 "skin, don't rewrite") and re-styled
   only via deep selectors below. */

/* Report-type segmented chips sit above the filter bar. */
.cashclear__modechips {
  margin-bottom: 18px;
}

/* Modal form: stacked inputs + action row spacing. */
.cashclear__form >>> .form-group {
  margin-bottom: 16px;
}
.cashclear__form .calm__modal__actions {
  margin-top: 8px;
}

/* The free-text search box reuses the Kit's .ed input look. */
.cashclear >>> .ed {
  width: 100%;
}

/* Skin the existing <el-table> as a Calm surface — deep selectors only,
   no column/slot changes (§11). */
.cashclear >>> .el-table {
  color: var(--c-text, #2c3e50);
  background: transparent;
}
.cashclear >>> .el-table th,
.cashclear >>> .el-table .thead-light th {
  background: var(--c-surface-soft, #f4f7fb);
  color: var(--c-text-soft, #5b6b7e);
  font-weight: 600;
  border-bottom: 1px solid var(--c-border, #e3e9f0);
}
.cashclear >>> .el-table td {
  border-bottom: 1px solid var(--c-border, #eef2f7);
}
.cashclear >>> .el-table::before {
  background: var(--c-border, #e3e9f0);
}
.cashclear >>> .el-table__row:hover > td {
  background: var(--c-surface-soft, #f7fafd) !important;
}
</style>
